import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { inr } from "@/lib/format";
import { Users, CheckCircle2, AlertCircle, TrendingUp, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!stats) return <div className="p-8 text-slate-500">Loading...</div>;

  const cards = [
    { label: "Total Tenants", value: stats.total_tenants, icon: Users, color: "bg-blue-50 text-blue-700" },
    { label: "Active Tenants", value: stats.active_tenants, icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700" },
    { label: "Expired", value: stats.expired_tenants, icon: AlertCircle, color: "bg-amber-50 text-amber-700" },
    { label: "Suspended", value: stats.suspended_tenants, icon: AlertCircle, color: "bg-rose-50 text-rose-700" },
    { label: "Subscription Revenue", value: inr(stats.subscription_revenue), icon: TrendingUp, color: "bg-violet-50 text-violet-700" },
  ];

  return (
    <div className="p-8">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="admin-dashboard-title">Platform Overview</h1>
        <p className="text-slate-500 mt-1 text-sm">Live snapshot of all tenants and subscription activity.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="p-5 border-slate-200" data-testid={`admin-stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className={`w-9 h-9 rounded-md grid place-items-center ${c.color}`}><Icon className="h-5 w-5" /></div>
              <div className="text-2xl font-display font-bold mt-3">{c.value}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            </Card>
          );
        })}
      </div>

      <Card className="mt-8 p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-4 w-4 text-slate-500" />
          <h3 className="font-display font-semibold">Tenants per plan</h3>
        </div>
        {stats.plan_distribution.length === 0 && <p className="text-sm text-slate-500">No tenants on plans yet.</p>}
        <div className="space-y-3">
          {stats.plan_distribution.map((p) => (
            <div key={p.plan_id || "none"} className="flex items-center gap-4">
              <div className="w-32 text-sm text-slate-700 font-medium">{p.plan_name}</div>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-900" style={{ width: `${Math.min(100, p.count * 20)}%` }} />
              </div>
              <div className="text-sm font-mono text-slate-600">{p.count}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
